import { useState, useCallback, useMemo } from 'react'
import { View } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import type { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { TriangleAlert } from 'lucide-react-native'
import { useTranslation } from 'react-i18next'
import { colors } from '@chillout/core'
import { Button } from '@chillout/ui'
import { supabase } from '../../lib/supabase'
import type { AdminAuthStackParamList } from '../../navigation/types'
import { AdminPlaceholderScreen } from './AdminPlaceholderScreen'
import { AdminProtectedRoute } from './AdminProtectedRoute'
import { AuditLogScreen } from './auditLog'
import { BirthdayRewardScreen } from './birthdayReward'
import { HomeScreen } from './home'
import { LoyaltyScreen } from './loyalty'
import { MembersScreen } from './members'
import { OperationsScreen } from './operations'
import { RecentActivityScreen } from './recentActivity'
import { SettingsScreen } from './settings'
import { AdminMenu } from './menu'
import { AdminModal, ModalActions } from './shared/Modal'
import type { AdminSectionRouteName, AdminSectionParams } from './adminRoutes'
import { AdminShellContext } from './AdminShellContext'
import type { AdminShellNavigate } from './AdminShellContext'
import { UpdateBanner } from './shared/UpdateBanner'
import { useVersionCheck } from './shared/useVersionCheck'

interface AdminShellProps {
  initialRouteName: AdminSectionRouteName
}

interface ActiveRoute<R extends AdminSectionRouteName = AdminSectionRouteName> {
  name: R
  params: AdminSectionParams[R]
}

interface PendingRoute<R extends AdminSectionRouteName = AdminSectionRouteName> {
  name: R
  params: AdminSectionParams[R]
}

export function AdminShell({ initialRouteName }: AdminShellProps) {
  const navigation = useNavigation<NativeStackNavigationProp<AdminAuthStackParamList>>()
  const { t } = useTranslation()
  const [activeRoute, setActiveRoute] = useState<ActiveRoute>({ name: initialRouteName, params: undefined })
  const [isDirty, setIsDirty] = useState(false)
  const [pendingRoute, setPendingRoute] = useState<PendingRoute | null>(null)

  const handleDirtyChange = useCallback((dirty: boolean) => {
    setIsDirty(dirty)
  }, [])

  const navigateTo: AdminShellNavigate = useCallback((route, ...args) => {
    const params = args[0] as AdminSectionParams[typeof route]
    setIsDirty(false)
    setActiveRoute({ name: route, params })
  }, [])

  function handleRoutePress(route: AdminSectionRouteName) {
    if (isDirty && route !== activeRoute.name) {
      setPendingRoute({ name: route, params: undefined })
      return
    }
    navigateTo(route)
  }

  function handleKeepEditing() {
    setPendingRoute(null)
  }

  function handleDiscardChanges() {
    if (!pendingRoute) return
    setPendingRoute(null)
    navigateTo(pendingRoute.name)
  }

  async function handleSignOut() {
    await supabase.rpc('log_admin_auth_event', { p_event_type: 'admin_signed_out' })
    await supabase.auth.signOut()
    navigation.replace('SignIn')
  }

  const { hasUpdate, dismiss } = useVersionCheck()

  const contextValue = useMemo(() => ({ navigateTo }), [navigateTo])

  const recentActivityParams = activeRoute.name === 'AdminRecentActivity'
    ? (activeRoute.params as AdminSectionParams['AdminRecentActivity'])
    : undefined

  function handleRefresh() {
    if (typeof window !== 'undefined') window.location.reload()
  }

  return (
    <AdminShellContext.Provider value={contextValue}>
      <AdminProtectedRoute>
        {(adminAccount) => (
          <AdminMenu
            activeRouteName={activeRoute.name}
            adminAccount={adminAccount}
            onRoutePress={handleRoutePress}
            onSignOut={handleSignOut}
          >
            {hasUpdate ? <UpdateBanner onRefresh={handleRefresh} onDismiss={dismiss} /> : null}
            {activeRoute.name === 'AdminHome' ? (
              <HomeScreen />
            ) : activeRoute.name === 'AdminMembers' ? (
              <MembersScreen />
            ) : activeRoute.name === 'AdminRecentActivity' ? (
              <RecentActivityScreen initialMemberQuery={recentActivityParams?.initialMemberQuery} />
            ) : activeRoute.name === 'AdminLoyalty' ? (
              <LoyaltyScreen onDirtyChange={handleDirtyChange} />
            ) : activeRoute.name === 'AdminBirthdayReward' ? (
              <BirthdayRewardScreen onDirtyChange={handleDirtyChange} />
            ) : activeRoute.name === 'AdminCashiersDevices' ? (
              <OperationsScreen />
            ) : activeRoute.name === 'AdminAuditLog' ? (
              <AuditLogScreen />
            ) : activeRoute.name === 'AdminSettings' ? (
              <SettingsScreen onDirtyChange={handleDirtyChange} />
            ) : (
              <AdminPlaceholderScreen routeName={activeRoute.name} />
            )}

            <AdminModal
              visible={pendingRoute !== null}
              onClose={handleKeepEditing}
              closeAccessibilityLabel={t('settings.discardChanges.closeAccessibilityLabel')}
              title={t('settings.discardChanges.title')}
              subtitle={t('settings.discardChanges.body')}
              icon={
                <View className="h-16 w-16 items-center justify-center rounded-full bg-warning/10">
                  <TriangleAlert size={32} color={colors.warning} strokeWidth={2} />
                </View>
              }
              actions={
                <ModalActions className="justify-between">
                  <Button
                    label={t('settings.discardChanges.keepEditing')}
                    variant="secondary"
                    fullWidth={false}
                    onPress={handleKeepEditing}
                  />
                  <Button
                    label={t('settings.discardChanges.discard')}
                    fullWidth={false}
                    onPress={handleDiscardChanges}
                  />
                </ModalActions>
              }
            >
              <View />
            </AdminModal>
          </AdminMenu>
        )}
      </AdminProtectedRoute>
    </AdminShellContext.Provider>
  )
}